import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PaperPlaneTilt, InstagramLogo, FacebookLogo, Phone, MapPin } from "@phosphor-icons/react";
import { properties } from "@/data/properties";
import Seo, { SITE } from "@/components/site/Seo";
import { breadcrumbJsonLd } from "@/lib/schema";
import { trackLead } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", property_name: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      trackLead({ form: "contact", property: form.property_name || undefined });
      toast.success("Thanks! Your message is on its way — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", property_name: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "bg-transparent border-b border-emerald/25 py-3 text-emerald placeholder:text-emerald-soft/50 focus:border-sunset focus:outline-none transition-colors duration-300 w-full";

  return (
    <main className="pt-32 md:pt-40 pb-24 md:pb-32">
      <Seo
        title="Contact | Double Rainbow Suites — Wilton Manors & Fort Lauderdale"
        description="Get in touch with Double Rainbow Suites to plan your stay in Wilton Manors or Fort Lauderdale. We answer guest messages in minutes."
        canonical={`${SITE}/contact`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          url: `${SITE}/contact`,
          about: { "@id": `${SITE}/#business` },
        }}
      />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="overline text-sunset mb-5">Get in touch</p>
          <h1 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight">
            Let's plan<br />your stay.
          </h1>
          <p className="text-lg text-emerald-soft max-w-md mt-6 font-light">
            Questions about a property, longer stays or bringing a pet? Send us a note — we usually reply within minutes.
          </p>

          <div className="mt-12 space-y-6">
            <div className="flex items-center gap-4">
              <span className="w-11 h-11 rounded-full bg-sand-2 grid place-items-center"><MapPin size={20} className="text-sunset" /></span>
              <span className="text-emerald">1425 NE 24th St, Wilton Manors, FL 33305</span>
            </div>
            <a data-testid="contact-phone-link" href="tel:+19543325725" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full bg-sand-2 grid place-items-center"><Phone size={20} className="text-sunset" /></span>
              <span className="text-emerald group-hover:text-sunset transition-colors duration-300">(954) 332-5725</span>
            </a>
            <a href="https://instagram.com/doublerainbowsuites" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full bg-sand-2 grid place-items-center"><InstagramLogo size={20} className="text-sunset" /></span>
              <span className="text-emerald group-hover:text-sunset transition-colors duration-300">@doublerainbowsuites</span>
            </a>
            <a href="https://www.facebook.com/people/Double-Rainbow-Suites/100078176427437/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
              <span className="w-11 h-11 rounded-full bg-sand-2 grid place-items-center"><FacebookLogo size={20} className="text-sunset" /></span>
              <span className="text-emerald group-hover:text-sunset transition-colors duration-300">Double Rainbow Suites</span>
            </a>
          </div>

          <a
            data-testid="contact-map"
            href="https://www.google.com/maps/search/?api=1&query=Double+Rainbow+Suites+1425+NE+24th+St+Wilton+Manors+FL+33305"
            target="_blank"
            rel="noopener noreferrer"
            className="group block mt-10 rounded-2xl overflow-hidden border border-emerald/10 relative"
            aria-label="Open Double Rainbow Suites location in Google Maps"
          >
            <iframe
              title="Double Rainbow Suites location map"
              src="https://www.google.com/maps?q=1425%20NE%2024th%20St%2C%20Wilton%20Manors%2C%20FL%2033305&output=embed"
              className="w-full h-[280px] block pointer-events-none"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <span className="absolute bottom-4 left-4 rounded-full bg-emerald text-sand text-xs font-medium px-4 py-2 opacity-0 translate-y-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0">
              Open in Google Maps →
            </span>
          </a>
        </motion.div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="border border-emerald/10 rounded-2xl p-8 md:p-10 bg-sand-2 space-y-7"
        >
          <div className="grid sm:grid-cols-2 gap-7">
            <div>
              <label className="overline text-emerald-soft">Name *</label>
              <input data-testid="contact-name" className={inputCls} value={form.name} onChange={update("name")} placeholder="Your name" />
            </div>
            <div>
              <label className="overline text-emerald-soft">Email *</label>
              <input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={update("email")} placeholder="you@email.com" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-7">
            <div>
              <label className="overline text-emerald-soft">Phone</label>
              <input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={update("phone")} placeholder="Optional" />
            </div>
            <div>
              <label className="overline text-emerald-soft">Property</label>
              <select data-testid="contact-property" className={inputCls} value={form.property_name} onChange={update("property_name")}>
                <option value="">Any / not sure</option>
                {properties.map((p) => (
                  <option key={p.slug} value={p.name}>{p.shortName}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="overline text-emerald-soft">Message *</label>
            <textarea data-testid="contact-message" rows={5} className={`${inputCls} resize-none`} value={form.message} onChange={update("message")} placeholder="Tell us your dates and what you're looking for…" />
          </div>
          <button
            data-testid="contact-submit"
            type="submit"
            disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-sunset px-8 py-4 text-sand font-medium transition-colors duration-300 hover:bg-emerald disabled:opacity-60"
          >
            {loading ? "Sending…" : "Send message"}
            <PaperPlaneTilt size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.form>
      </div>
    </main>
  );
}
